import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, Info, Upload } from "lucide-react";
import { Button, useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { ReactComponent as DishMark } from "../../../assets/svg/sidebar/dishes.svg";
import { OPEN_BULK_UPLOAD, ROUTES } from "../../../routes/paths";
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import { useDishScope } from "../../mealPlans/hooks/useDishScope";
import { IntegrateAppsCard } from "../../appointments";
import type { DishScope } from "../../mealPlans/types/plans.types";

/**
 * One of the two ways the Dish search can be set, with a picture of what the
 * Dish field's list looks like under it. The picture is fixed: three rows
 * that show the difference — one of yours among the catalog's, or only yours
 * — rather than a live search, which would show whatever the workspace
 * happens to hold and might show nothing.
 */
interface ScopeChoice {
  scope: DishScope;
  title: string;
  description: string;
  /** Each row of the picture, and whether it wears the "yours" mark. */
  rows: { name: string; own: boolean }[];
}

const CHOICES: ScopeChoice[] = [
  {
    scope: "all",
    title: "All dishes",
    description: "Your dishes + Naurist database. Best when you want more choices.",
    rows: [
      { name: "Dosa", own: false },
      { name: "Ragi dosa", own: true },
      { name: "Masala dosa", own: false },
    ],
  },
  {
    scope: "mine",
    title: "My dishes only",
    description: "Only dishes you added. Faster when you plan with your own recipes.",
    rows: [
      { name: "Ragi dosa", own: true },
      { name: "Wheat dosa", own: true },
      { name: "Moong dal dosa", own: true },
    ],
  },
];

/**
 * What the Dish field's list looks like under a setting — its box with the
 * list open beneath, drawn the way `CustomDropdown` draws the real one, down
 * to the key to the mark pinned at the top.
 *
 * A picture of a control rather than a control: nothing in it can be pressed,
 * typed into or picked, and it says nothing to a screen reader. What it is
 * for is showing the difference between the two settings before one is
 * chosen, which the words above it can only describe.
 */
const ListPreview: React.FC<{ rows: ScopeChoice["rows"] }> = ({ rows }) => (
  <div
    aria-hidden="true"
    className="pointer-events-none mt-4 select-none rounded-md border border-line bg-white"
  >
    <div className="flex items-center justify-between px-3 py-2.5 text-[13px] text-ink-subtle">
      Search dish
      <ChevronUp className="h-4 w-4 text-ink-muted" />
    </div>
    <div className="mx-px mb-px rounded-b-md border-t border-line shadow-[0px_4px_12px_0px_#0000000F]">
      <div className="flex items-center gap-2 border-b border-[#E0E0E0] bg-[#F8FEFF] px-3 py-2 text-[12px] text-ink-muted">
        <DishMark className="h-4 w-4 flex-shrink-0" />
        <span>= Your dishes</span>
      </div>
      {rows.map((row) => (
        <div
          key={row.name}
          className="flex items-center justify-between px-3 py-2 text-[13px] text-ink"
        >
          {row.name}
          {row.own && <DishMark className="h-4 w-4 flex-shrink-0 text-brand" />}
        </div>
      ))}
    </div>
  </div>
);

/**
 * The radio's dot and the words beside it — the top of either card, drawn
 * apart from the card so the held one can keep it while giving up the button
 * around it.
 */
const ChoiceHeading: React.FC<{ choice: ScopeChoice; selected: boolean }> = ({
  choice,
  selected,
}) => (
  <div className="flex items-start gap-2.5">
    {/* The radio's dot, drawn rather than a native input so the whole card can
        be the button that ticks it. The dot is inset from every edge of the
        ring rather than centred by flex, so it cannot drift inside a button's
        own text layout: the same distance on all four sides is centred by
        definition. */}
    <span
      aria-hidden="true"
      className={`relative mt-[3px] h-4 w-4 flex-shrink-0 rounded-full border ${
        selected ? "border-brand" : "border-[#B8BFC7]"
      }`}
    >
      {selected && <span className="absolute inset-[3px] rounded-full bg-brand" />}
    </span>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[15px] 3xl:text-[16px] font-medium text-ink">
          {choice.title}
        </span>
        {choice.scope === "all" && (
          <span className="rounded-full bg-[#D7F9FD] px-2 py-0.5 text-[11px] font-medium text-[#007E8C]">
            Default
          </span>
        )}
      </div>
      <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
        {choice.description}
      </p>
    </div>
  </div>
);

/**
 * What stands where the list would be under "My dishes only" while the
 * workspace has no dish of its own: there is nothing to draw a picture of, so
 * the space says so and holds the one move that fills it.
 *
 * The search box above is still only a picture, as it is in `ListPreview`.
 * The box below it is not: the button in it is the only way out of a setting
 * that cannot be chosen yet, so it is reachable even though the card around
 * it is held.
 */
const EmptyDishPreview: React.FC<{ action: React.ReactNode }> = ({ action }) => (
  <div className="mt-4">
    <div
      aria-hidden="true"
      className="pointer-events-none flex select-none items-center justify-between rounded-md border border-line bg-white px-3 py-2.5 text-[13px] text-ink-subtle"
    >
      Search dish
      <ChevronUp className="h-4 w-4 text-ink-muted" />
    </div>
    <div className="mt-2 rounded-md border border-line bg-white px-5 py-5 text-center">
      <p className="text-[14px] 3xl:text-[15px] font-medium text-ink">
        No dish uploaded
      </p>
      <p className="mx-auto mt-1 max-w-[340px] text-[12px] 3xl:text-[13px] text-ink-muted">
        Upload dishes at once via spreadsheet with portions and instructions.
        Then, filter meal-plan search to your dishes only.
      </p>
      <div className="mt-4 flex justify-center">{action}</div>
    </div>
  </div>
);

/**
 * My brand → Configuration: how the plan builder behaves for this workspace.
 *
 * Under My brand rather than Settings, beside the form and the plans the
 * practice sells: it is a choice about how this practice writes its plans,
 * not about the account.
 *
 * One setting so far — which dishes the Dish field searches — and the same
 * one the switch on the plan builder's header flips; both read and write
 * `useDishScope`, so a change here is on every open plan at once.
 */
const BrandConfigurationTab: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { scope, setScope, canNarrow } = useDishScope();

  // Uploading a dish sheet writes into the workspace, so it is shut behind a
  // running plan exactly as it is on Dishes. The lock is answered here rather
  // than a page away: somebody whose trial has ended should hear it where
  // they pressed, not arrive on a screen whose button is grey for a reason
  // nobody has given them yet. See `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  const choose = (next: DishScope) => {
    if (next === scope) return;
    // The card is disabled when this is false, so an ordinary click never
    // gets here; a keyboard on a card still settling can.
    if (next === "mine" && canNarrow !== true) return;
    setScope(next).catch((reason) => {
      toast.error(
        "Could not change the dish search",
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-md border border-line bg-white px-5 py-4 3xl:px-6 3xl:py-5">
        <div className="min-w-0">
          <h2 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
            Dish search in meal plans
          </h2>
          <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
            Choose which dishes appear when you search for a dish while
            creating a meal plan.
          </p>
        </div>

        {/* Two cards and a radio in each: the whole card is the target, since
            the picture under the words is most of what is being chosen. */}
        <div
          role="radiogroup"
          aria-label="Dish search in meal plans"
          className="mt-5 grid gap-4 lg:grid-cols-2"
        >
          {CHOICES.map((choice) => {
            const selected = choice.scope === scope;
            // "My dishes only" waits on a dish to be only: without one the
            // card is drawn but cannot be picked. Still shown as selected if
            // the server holds it, so what is set is never hidden — the way
            // out is the other card.
            const unavailable = choice.scope === "mine" && canNarrow !== true;
            // Held for want of a dish rather than for want of an answer, and
            // known to be so: while the count is still being read the card is
            // merely held, and there is nothing to offer yet.
            const nothingToSearch =
              choice.scope === "mine" && canNarrow === false;

            if (nothingToSearch) {
              // The card gives up being one button so that it can hold one: a
              // button inside a disabled button cannot be pressed. The words
              // keep the radio's part — what is set stays legible, and the
              // group still has two of them — while the press that matters
              // here is the upload below.
              return (
                <div
                  key={choice.scope}
                  className="rounded-md border border-line bg-white p-4"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled
                    className="block w-full cursor-not-allowed text-left opacity-60"
                  >
                    <ChoiceHeading choice={choice} selected={selected} />
                  </button>
                  {/* The upload itself lives on Dishes — one modal, one
                      importer, one report — so this carries the intent there
                      rather than a second copy of it, and Dishes opens the
                      modal on arrival. A button and not a link, because
                      whether it goes anywhere at all depends on the lock. */}
                  <EmptyDishPreview
                    action={
                      // Text and an icon rather than a filled button: it
                      // stands inside the card's own outlined box, under two
                      // lines of grey explanation, and a solid teal block
                      // there reads as the point of the tab rather than as
                      // the way out of a setting that cannot be chosen yet.
                      //
                      // Under the pointer it tints the brand's pale teal
                      // rather than `ghost`'s grey, which is what Save as
                      // draft does on the plan builder — the two are the same
                      // kind of button and should answer a pointer the same
                      // way. Forced, because it is overruling the variant's
                      // own hover rather than adding to it, and class order
                      // in the attribute decides nothing.
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:!bg-[#E6FAFB]"
                        startIcon={<Upload className="h-4 w-4" />}
                        onClick={() =>
                          navigate(ROUTES.dishes, {
                            state: { [OPEN_BULK_UPLOAD]: true },
                          })
                        }
                        {...gate.props("upload dishes")}
                      >
                        Upload your dish
                      </Button>
                    }
                  />
                </div>
              );
            }

            return (
              <button
                key={choice.scope}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={unavailable}
                onClick={() => choose(choice.scope)}
                className={`rounded-md border p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-60 ${
                  selected
                    ? "border-[#5BD3E1] bg-[#F1FCFD]"
                    : "border-line bg-white enabled:hover:border-[#5BD3E1]"
                }`}
              >
                <ChoiceHeading choice={choice} selected={selected} />
                <ListPreview rows={choice.rows} />
              </button>
            );
          })}
        </div>

        {/* Inside the card, under the two choices it speaks for. It was a
            loose line below the card until there was a second card under it;
            then it sat between the two belonging to neither, which is worse
            than being one more thing inside the box — it is about this
            setting, and nothing else on the tab. */}
        <p className="mt-4 flex items-center gap-1.5 text-[12px] 3xl:text-[13px] text-ink-subtle">
          <Info aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
          Applies to all meal plans. You can also switch it from the meal plan
          screen anytime.
        </p>
      </section>

      {/* The second thing this tab sets, and not about plans at all: which
          meeting app makes the link for an online appointment. It lives on
          the same tab because it is the same kind of answer — how this
          practice works, set once and true everywhere — and the card itself
          belongs to the appointments feature that reads it. */}
      <IntegrateAppsCard />
    </div>
  );
};

export default BrandConfigurationTab;
